import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import TuneIcon from '@mui/icons-material/Tune';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import {
  Box,
  Button,
  Divider,
  Grid,
  Pagination,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { AxiosError } from 'axios';
import {
  gray10,
  gray100,
  gray70,
  gray80,
  gray90,
  primaryColor,
  white,
  yellow
} from '../../../constants/Colors';
import {
  productMethodList,
  productOrderList
} from '../../../constants/SelectOptions';
import { common } from '../../../modules/error';
import CustomSelectField from '../../uiParts/CustomSelectField';
import CustomTextfield from '../../uiParts/CustomTextfield';
import CustomRadioButton from '../../uiParts/CustomRadioButton';
import CustomCheckBox from '../../uiParts/CustomCheckBox';
import SideBar from '../../uiParts/SideBar';
import { sideBarWidth, spHeaderHeight } from '../../../constants/Location';
import MaterialCard, { ItemProps } from '../../uiParts/MaterialCard';
import { Controller, useForm } from 'react-hook-form';
import { ProductGetListType } from '../../../validation/products/productType';
import MaterialGraph from '../../uiParts/MaterialGraph';
import { AuthContext } from '../../../contexts/auth';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import { response } from '../../../api/db';
import { getProductList } from '../../../api/db/product';
import { AxiosResponse } from 'axios';
import { getCategoryList } from '../../../api/db/category';
import { getMaterialList } from '../../../api/db/material';
import { addMylist, deleteMylistProduct } from '../../../api/db/mylist';
import { addFavorite, deleteFavorite } from '../../../api/db/favorite';
import SearchIcon from '@mui/icons-material/Search';
import { yupResolver } from '@hookform/resolvers/yup';
import { productSearchSchema } from '../../../validation/products';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  buttonWidth: {
    '@media screen and (max-width: 600px)': {
      width: '100%'
    },
    width: '200px'
  },
  containerWrapper: {
    '@media screen and (max-width: 600px)': {
      m: '16px'
    },
    flexWrap: 'nowrap',
    m: '0 32px 32px 32px',
    width: 'unset'
  },
  icon: {
    width: '80px',
    height: '80px',
    pb: '16px',
    '@media screen and (max-width:600px)': {
      width: '64px',
      height: '64px'
    }
  },
  listGraphButton: {
    border: `1px solid ${primaryColor}`,
    width: '40px'
  },
  listGraphIcon: {
    color: primaryColor
  },
  listGraphToggle: {
    height: '40px'
  },
  noWrap: {
    whiteSpace: 'nowrap'
  },
  pagination: {
    '& .MuiPagination-ul': {
      justifyContent: 'center'
    },
    '& .MuiButtonBase-root.MuiPaginationItem-root': {
      '&.Mui-selected': {
        backgroundColor: primaryColor,
        color: white
      }
    },
    color: gray100
  },
  paginationWrapper: {
    m: '80px auto'
  },
  searchBox: {
    bgcolor: gray10,
    border: `1px solid ${gray70}`,
    height: 'fit-content',
    p: '24px 16px 16px',
    width: '280px'
  },
  searchBoxSp: {
    bgcolor: gray10,
    height: 'fit-content',
    p: '16px 16px 60.5px',
    width: '100%'
  },
  searchDivider: {
    color: gray70
  },
  searchItemName: {
    color: gray100,
    fontSize: '18px',
    fontWeight: 500
  },
  searchItemWrapper: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
    m: '24px 0'
  },
  searchItemWrapperSp: {
    display: 'flex',
    flexDirection: 'column',
    m: '24px 0'
  },
  serachOptionWrapper: {
    ml: '9px'
  },
  searchSpBox: {
    '@media screen and (max-width: 1199px)': {
      display: 'flex'
    },
    alignItems: 'center',
    bgcolor: gray10,
    border: `1px solid ${gray70}`,
    boxSizing: 'border-box',
    display: 'none',
    height: '56px',
    p: '16px'
  },
  searchSpBoxArrowIcon: {
    color: gray90,
    height: '24px',
    width: '24px'
  },
  searchSpBoxText: {
    color: gray100,
    fontSize: '16px',
    fontWeight: 500
  },
  searchSpBoxTuneIcon: {
    color: gray100,
    height: '24px',
    width: '24px'
  },
  searchTitle: {
    color: gray100,
    fontSize: '22px',
    fontWeight: 400
  },
  searchTitleWrapper: {
    mb: '24px'
  },
  star: {
    color: yellow,
    height: '24px',
    width: '24px',
    verticalAlign: 'sub'
  },
  starOutline: {
    color: gray80,
    height: '24px',
    width: '24px',
    verticalAlign: 'sub'
  }
};

const MaterialsList: React.FC = () => {
  const co2BasisOptions = [
    {
      option: '第三者認証',
      value: 1
    },
    {
      option: '自社算定',
      value: 2
    }
  ];

  const carbonOffsets = [
    {
      option: 'オフセットを含む',
      value: 1
    }
  ];

  const certificationFlgs = [
    {
      label: '認定企業のみ',
      value: 2
    },
    {
      label: 'すべて',
      value: 1
    }
  ];

  const recomendMaterials = [
    {
      option: 'おすすめ建材のみ表示',
      value: 1
    }
  ];

  const favoriteMaterials = [
    {
      option: 'お気に入り建材のみ表示',
      value: 1
    }
  ];

  const evaluationOptions = [
    {
      label: 'すべて',
      value: 1
    },
    {
      label: (
        <>
          <StarOutlinedIcon sx={styles.star} />
          <StarOutlinedIcon sx={styles.star} />
          <StarOutlinedIcon sx={styles.star} />
          <StarOutlinedIcon sx={styles.star} />
          <StarOutlineOutlinedIcon sx={styles.starOutline} />
          <Typography component="span" sx={{ color: gray100, ml: '8px' }}>
            以上
          </Typography>
        </>
      ),
      value: 4
    }
  ];

  // リストとグラフの切り替え
  const navigate = useNavigate();
  const location = useLocation();

  const alignment = useMemo(
    () => (location.pathname === '/material/graph' ? 'graph' : 'list'),
    [location.pathname]
  );

  // ヘッダーのソート
  const [selectedOrder, setSelectedOrder] = useState<number>(
    Number(localStorage.getItem('selectedOrder')) || 1
  );
  const handleChangeOrder = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as number;
    setSelectedOrder(value);
    localStorage.setItem('selectedOrder', String(value));
  };

  // 計算方法
  const [selectedMethod, setSelectedMethod] = useState(
    Number(localStorage.getItem('selectedMethod')) || 1
  );
  const handleChangeMethod = (event: SelectChangeEvent<unknown>) => {
    const selectedMethod = productMethodList.find(
      (method) => method.value === event.target.value
    );
    window.dataLayer.push({
      event: 'co2_method',
      screen: '/material',
      category: 'pulldown',
      action: 'material_co2_method',
      label: selectedMethod?.option,
      user_id: userId,
      user_type: userType,
      user_rank: userRank
    });

    const value = event.target.value as number;
    setSelectedMethod(value);
    localStorage.setItem('selectedMethod', String(value));
  };

  const [list, setList] = React.useState<ItemProps[]>([]);
  const [categoryList, setCategoryList] = React.useState<
    { value: number; option: string }[]
  >([]);
  const [materialList, setMaterialList] = React.useState<
    { value: number; option: string }[]
  >([]);
  const { checkLoginStatus, userId, userRank, userType } =
    useContext(AuthContext);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [errorText, setErrorText] = React.useState<string>('');
  const [infoText, setInfoText] = React.useState<string>('');
  const [open, setOpen] = React.useState<boolean>(false);
  const [total, setTotal] = React.useState<number>(0);
  const [paginationLength, setPaginationLength] = React.useState<number>(0);
  const [page, setPage] = React.useState<number>(1);
  const [currentCount, setCurrentCount] = React.useState<{
    before: number;
    after: number;
  }>({
    before: 0,
    after: 0
  });
  const topRef = useRef<HTMLDivElement>(null);
  const [firstLoading, setFirstLoading] = React.useState<boolean>(true);

  const limit = 20;

  const productsList = (
    products: AxiosResponse<ProductGetListType>
  ): ItemProps[] => {
    const list = products.data.products.map((product) => {
      const sortedImageData = product.image.sort(
        (a, b) => ((a && a.sort) || 0) - ((b && b.sort) || 0)
      );

      return {
        productId: product.id,
        productInfoId: product.product_info_id,
        status: 1,
        alias: product.alias,
        category: product.category.name,
        material: product.material.name,
        name: product.name,
        specification: product.specification,
        summary: product.summary,
        emissionIntensity: product.emission_intensity,
        reduceRate: product.reduce_rate,
        calculationValue: product.emission_intensity,
        calculationBasisType: [product.calculation_basis_type],
        calculationBasisName: product.calculation_basis_name,
        company: product.users.company,
        companyShort: product.users.company_short,
        myListNum: product.mylist_number,
        score: product.evaluation_score,
        recommendFlg: [product.recommend_flg],
        title: product.evaluations.title,
        content: product.evaluations.content,
        updateDate: product.updated_at,
        myListFlg: Boolean(product.mylist_flg),
        favoriteFlg: Boolean(product.favorite_flg),
        referenceUnitPrice: product.reference_unit_price,
        reductionAmount: product.calculated_emission_intensity,
        price: product.reference_unit_price ?? 0,
        images: sortedImageData,
        co2Unit: product.material.co2_unit,
        costUnit: product.material.cost_unit,
        calculatedEmissionIntensity: product.calculated_emission_intensity,
        calculatedMethod: product.calculated_method,
        calculatedReduceRate: product.calculated_reduce_rate,
        carbonOffsetFlg: product.carbon_offset_flg,
        certificationFlg: product.certification_flg
      };
    });
    return list;
  };

  const setListPagination = async (): Promise<void> => {
    try {
      setLoading(true);
      await getList();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      setErrorText(error.message);
      setOpen(true);
      throw new Error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 建材リストを取得
  const getList = async () => {
    const offset = (page - 1) * limit;
    // 建材リストを取得
    const fetchedData = await getProductList(
      limit,
      offset,
      selectedOrder,
      selectedMethod,
      Number(watch('categoryId')),
      Number(watch('materialId')),
      watch('freeWord'),
      watch('calculationBasisType')
        .map((value, index) => (value ? index + 1 : null))
        .filter((x) => x != null),
      watch('carbonOffsetFlg')
        .map((value, index) => (value ? index + 1 : null))
        .filter((x) => x != null)[0],
      watch('certificationFlg'),
      watch('recommendFlg')
        .map((value, index) => (value ? index + 1 : null))
        .filter((x) => x != null)[0],
      watch('favoriteMaterialsFlg')
        .map((value, index) => (value ? index + 1 : null))
        .filter((x) => x != null)[0],
      watch('evaluation')
    );
    const newProductList = productsList(fetchedData);
    setList(newProductList);

    // ページネーション
    setTotal(fetchedData.data.total_count);
    setPaginationLength(Math.ceil(fetchedData.data.total_count / limit));
    setCurrentCount({
      before: (page - 1) * limit + 1,
      after:
        page * limit > fetchedData.data.total_count
          ? fetchedData.data.total_count
          : page * limit
    });
  };

  const {
    control,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      calculationBasisType: Array(co2BasisOptions.length).fill(false),
      carbonOffsetFlg: Array(carbonOffsets.length).fill(false),
      recommendFlg: Array(recomendMaterials.length).fill(false),
      favoriteMaterialsFlg: Array(favoriteMaterials.length).fill(false)
    },
    resolver: yupResolver(productSearchSchema)
  });

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    if (newAlignment === 'graph') {
      navigate('/material/graph');
    } else {
      navigate('/material');
    }
  };

  // 計算方法かソート順を変更した時、建材リストを取得
  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        if (checkLoginStatus) {
          const result = await checkLoginStatus();
          // 未ログイン時はログイン画面に飛ばす
          if (!result.check) return navigate('/auth/login');

          setValue('calculationBasisType', [true, true, true]);
          setValue('certificationFlg', 1);
          setValue('evaluation', 1);
          await getList();
          const categories = await getCategoryList();
          const categoriesList = categories.data
            .map((category: { id: number; name: string; sort: number }) => {
              return {
                value: category.id,
                option: category.name,
                sort: category.sort
              };
            })
            .sort(
              (a: { sort: number }, b: { sort: number }) => a.sort - b.sort
            );
          setCategoryList(categoriesList);
        } else {
          navigate('/auth/login');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        setErrorText(error.message);
        setOpen(true);
      } finally {
        setFirstLoading(false);
        setLoading(false);
      }
    })();
  }, []);

  const sortChange = async () => {
    try {
      setLoading(true);
      if (checkLoginStatus) {
        const result = await checkLoginStatus();
        // 未ログイン時はログイン画面に飛ばす
        if (!result.check) return navigate('/auth/login');
        await getList();
        const categories = await getCategoryList();
        const categoriesList = categories.data
          .map((category: { id: number; name: string; sort: number }) => {
            return {
              value: category.id,
              option: category.name,
              sort: category.sort
            };
          })
          .sort((a: { sort: number }, b: { sort: number }) => a.sort - b.sort);
        setCategoryList(categoriesList);
        window.dataLayer.push({
          event: 'click',
          screen: '/material',
          category: 'pulldown',
          action: 'material_sort',
          user_id: userId,
          user_type: userType,
          user_rank: userRank
        });
      } else {
        navigate('/auth/login');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      setErrorText(error.message);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void (async () => {
      await sortChange();
    })();
  }, [selectedOrder]);

  useEffect(() => {
    void (async () => {
      await sortChange();
      setPage(1);
    })();
  }, [selectedMethod]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        setValue('materialId', undefined);
        const materials = await getMaterialList();
        console.log(materials);
        const filteredMaterials = materials.data.filter(
          (material: { id: number; category_id: number; name: string }) =>
            material.category_id === watch('categoryId')
        );
        const materialList: { value: number; option: string }[] = [];
        filteredMaterials.map(
          (material: { id: number; category_id: number; name: string }) => {
            materialList.push({
              value: material.id,
              option: material.name
            });
          }
        );
        setMaterialList(materialList);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
      }
    })();
  }, [watch('categoryId')]);

  useEffect(() => {
    void (async () => {
      await setListPagination();
      topRef?.current?.scrollIntoView();
    })();
  }, [page]);

  const clearSearch = () => reset();

  const [searchSpBoxOpen, setSearchSpBoxOpen] = React.useState(false);

  const searchSpBoxToggle = () => {
    setSearchSpBoxOpen(!searchSpBoxOpen);
  };

  const search = async () => {
    try {
      setLoading(true);
      await getList();
      setPage(1);
      const dataToPush = {
        select_order: selectedOrder,
        select_method: selectedMethod,
        category: watch('categoryId'),
        material: watch('materialId'),
        freeWord: watch('freeWord'),
        calculation_basis_type: watch('calculationBasisType'),
        carbon_offset_flg: watch('carbonOffsetFlg'),
        certification_flg: watch('certificationFlg'),
        recommend_flg: watch('recommendFlg'),
        favorite_materials_flg: watch('favoriteMaterialsFlg'),
        evaluation: watch('evaluation')
      };
      window.dataLayer.push({
        event: 'search',
        screen: '/material',
        category: 'button',
        action: 'material_search',
        value: JSON.stringify(dataToPush),
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      setErrorText(error.message);
      setOpen(true);
    } finally {
      if (searchSpBoxOpen) {
        setSearchSpBoxOpen(false);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    void (async () => {
      if (!firstLoading) await search();
    })();
  }, [
    watch('categoryId'),
    watch('materialId'),
    watch('freeWord'),
    watch('calculationBasisType'),
    watch('carbonOffsetFlg'),
    watch('certificationFlg'),
    watch('recommendFlg'),
    watch('evaluation'),
    watch('favoriteMaterialsFlg')
  ]);

  // マイリスト追加
  const addMylistFun = async (productId: number, productName: string) => {
    try {
      setOpen(false);
      setInfoText('');
      setErrorText('');
      const res = await addMylist(productId);
      setInfoText(`マイリスト「${res.data.title}」に追加しました`);
      window.dataLayer.push({
        event: 'mylist',
        screen: '/material',
        category: 'button',
        action: 'material_mylist',
        label: productName,
        value: productId,
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
      } else {
        setErrorText(common);
      }
    } finally {
      await getList();
      setOpen(true);
    }
  };

  // マイリスト削除
  const deleteMylistFun = async (productId: number, productName: string) => {
    try {
      setOpen(false);
      setInfoText('');
      setErrorText('');
      const res = await deleteMylistProduct(productId);
      setInfoText(`マイリスト「${res.data.title}」から削除しました`);
      window.dataLayer.push({
        event: 'mylist_del',
        screen: '/material',
        category: 'button',
        action: 'material_mylist_del',
        label: productName,
        value: productId,
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
      } else {
        setErrorText(common);
      }
    } finally {
      await getList();
      setOpen(true);
    }
  };

  // お気に入り追加
  const addFavoriteFun = async (productId: number, productName: string) => {
    try {
      setOpen(false);
      setInfoText('');
      setErrorText('');
      await addFavorite(productId);
      setInfoText('お気に入りに追加しました');

      window.dataLayer.push({
        event: 'favorite',
        screen: '/material',
        category: 'button',
        action: 'material_favorite',
        label: productName,
        value: productId,
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
      } else {
        setErrorText(common);
      }
    } finally {
      await getList();
      setOpen(true);
    }
  };

  // お気に入り削除
  const deleteFavoriteFun = async (productId: number, productName: string) => {
    try {
      setOpen(false);
      setInfoText('');
      setErrorText('');
      await deleteFavorite(productId);
      setInfoText('お気に入りから削除しました');
      window.dataLayer.push({
        event: 'favorite_del',
        screen: '/material',
        category: 'button',
        action: 'material_favorite_del',
        label: productName,
        value: productId,
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
      } else {
        setErrorText(common);
      }
    } finally {
      await getList();

      setOpen(true);
    }
  };

  return (
    <>
      <CustomHead
        title={`${
          alignment === 'graph' ? '建材一覧(グラフ)' : '建材一覧'
        } | CMCOS[コムコス]`}
      />
      <Grid
        container
        flexDirection={'row'}
        sx={{
          '@media screen and (max-width: 960px)': {
            flexDirection: 'column'
          },
          flexDirection: 'row'
        }}
      >
        <SideBar />
        <Grid
          item
          sx={{
            '@media screen and (max-width: 960px)': {
              height: `calc(100vh - ${spHeaderHeight})`,
              ml: 0
            },
            flex: 1,
            height: '100vh',
            ml: sideBarWidth,
            overflowY: 'scroll',
            overflowX: 'auto'
          }}
        >
          {/* 絞り込みボタン for SP */}
          <Box sx={styles.searchSpBox} onClick={searchSpBoxToggle}>
            <Grid container flexDirection={'row'} gap={'12px'}>
              {searchSpBoxOpen ? (
                <KeyboardArrowLeftIcon sx={styles.searchSpBoxTuneIcon} />
              ) : (
                <TuneIcon sx={styles.searchSpBoxTuneIcon} />
              )}
              <Typography sx={styles.searchSpBoxText}>絞り込み</Typography>
            </Grid>
            <Grid>
              {searchSpBoxOpen ? (
                <Button
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSearch();
                  }}
                >
                  クリア
                </Button>
              ) : (
                <KeyboardArrowRightIcon sx={styles.searchSpBoxArrowIcon} />
              )}
            </Grid>
          </Box>

          {/* 絞り込みボックス for SP */}
          <Box
            sx={{
              '@media screen and (max-width: 1199px)': {
                display: searchSpBoxOpen ? 'block' : 'none'
              },
              display: 'none'
            }}
          >
            <>
              <CustomLoading loading={loading} />
              <Grid item sx={styles.searchBoxSp}>
                <Box sx={[styles.searchItemWrapperSp, { mt: '0px' }]}>
                  <Typography sx={styles.searchItemName}>カテゴリ</Typography>
                  <Box sx={{ mt: '8px' }}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          placeholder="大分類を選択"
                          defaultValue={0}
                          list={categoryList}
                        />
                      )}
                      control={control}
                      name="categoryId"
                    />
                  </Box>
                  <Box sx={{ mt: '8px' }}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          placeholder="部材名を選択"
                          disabled={watch('categoryId') === (0 || undefined)}
                          defaultValue={0}
                          list={materialList}
                        />
                      )}
                      control={control}
                      name="materialId"
                    />
                  </Box>
                </Box>
                <Divider sx={styles.searchDivider} />
                <Box sx={styles.searchItemWrapperSp}>
                  <Typography sx={styles.searchItemName}>
                    フリーワード
                  </Typography>

                  <Box sx={{ mt: '8px' }}>
                    <Controller
                      render={({ field }) => (
                        <CustomTextfield
                          {...field}
                          placeholder="フリーワードを入力"
                          width={'100%'}
                          error={Boolean(errors.freeWord)}
                          helperText={errors.freeWord?.message}
                        />
                      )}
                      control={control}
                      name="freeWord"
                    />
                  </Box>
                </Box>
                <Divider sx={styles.searchDivider} />

                <Box sx={styles.searchItemWrapperSp}>
                  <Typography sx={styles.searchItemName}>
                    CO2算定の根拠
                  </Typography>
                  <Box sx={styles.serachOptionWrapper}>
                    <Controller
                      render={({ field }) => (
                        <CustomCheckBox
                          {...field}
                          list={co2BasisOptions}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                      control={control}
                      name="calculationBasisType"
                    />
                  </Box>
                </Box>

                <Box sx={[styles.searchItemWrapperSp, { m: '0 0 24px 0' }]}>
                  <Typography sx={styles.searchItemName}>
                    CO2算定の手段
                  </Typography>
                  <Box sx={styles.serachOptionWrapper}>
                    <Controller
                      render={({ field }) => (
                        <CustomCheckBox
                          {...field}
                          list={carbonOffsets}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                      control={control}
                      name="carbonOffsetFlg"
                    />
                  </Box>
                </Box>
                <Divider sx={styles.searchDivider} />
                <Box sx={styles.searchItemWrapperSp}>
                  <Typography sx={styles.searchItemName}>
                    企業の環境認定
                  </Typography>
                  <Box sx={styles.serachOptionWrapper}>
                    <Controller
                      render={({ field }) => (
                        <CustomRadioButton
                          {...field}
                          options={certificationFlgs}
                        />
                      )}
                      control={control}
                      name="certificationFlg"
                    />
                  </Box>
                </Box>
                <Divider sx={styles.searchDivider} />
                <Box sx={styles.searchItemWrapperSp}>
                  <Typography sx={styles.searchItemName}>
                    おすすめ建材
                  </Typography>
                  <Box sx={styles.serachOptionWrapper}>
                    <Controller
                      render={({ field }) => (
                        <CustomCheckBox
                          {...field}
                          list={recomendMaterials}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                      control={control}
                      name="recommendFlg"
                    />
                  </Box>
                </Box>
                <Divider sx={styles.searchDivider} />
                <Box sx={styles.searchItemWrapper}>
                  <Typography sx={styles.searchItemName}>
                    お気に入り建材
                  </Typography>
                  <Box sx={[styles.serachOptionWrapper, styles.noWrap]}>
                    <Controller
                      render={({ field }) => (
                        <CustomCheckBox
                          {...field}
                          list={favoriteMaterials}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                      control={control}
                      name="favoriteMaterialsFlg"
                    />
                  </Box>
                </Box>
                <Divider sx={styles.searchDivider} />
                <Box sx={styles.searchItemWrapperSp}>
                  <Typography sx={styles.searchItemName}>評価</Typography>
                  <Box sx={styles.serachOptionWrapper}>
                    <Controller
                      render={({ field }) => (
                        <CustomRadioButton
                          {...field}
                          options={evaluationOptions}
                        />
                      )}
                      control={control}
                      name="evaluation"
                    />
                  </Box>
                </Box>
              </Grid>
            </>
          </Box>

          <Grid
            container
            flexDirection={'row'}
            columnGap={'32px'}
            sx={[
              styles.containerWrapper,
              {
                '@media screen and (max-width: 1199px)': {
                  display: searchSpBoxOpen ? 'none' : 'block'
                }
              }
            ]}
          >
            {/* 絞り込みボックス for PC */}
            <Box
              sx={{
                '@media screen and (max-width: 1199px)': {
                  display: 'none'
                },
                display: 'block',
                mt: '32px'
              }}
            >
              <>
                <CustomLoading loading={loading} />
                <Grid item sx={styles.searchBox}>
                  {
                    <>
                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={styles.searchTitleWrapper}
                      >
                        <Typography sx={styles.searchTitle}>
                          絞り込み
                        </Typography>
                        <Button variant="text" onClick={() => clearSearch()}>
                          クリア
                        </Button>
                      </Box>
                      <Divider sx={styles.searchDivider} />
                    </>
                  }
                  <Box
                    sx={{
                      ...styles.searchItemWrapper
                    }}
                  >
                    <Typography sx={styles.searchItemName}>カテゴリ</Typography>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          placeholder="大分類を選択"
                          defaultValue={0}
                          list={categoryList}
                        />
                      )}
                      control={control}
                      name="categoryId"
                    />
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          placeholder="部材名を選択"
                          disabled={watch('categoryId') === (0 || undefined)}
                          defaultValue={0}
                          list={materialList}
                        />
                      )}
                      control={control}
                      name="materialId"
                    />
                  </Box>
                  <Divider sx={styles.searchDivider} />
                  <Box sx={styles.searchItemWrapper}>
                    <Typography sx={styles.searchItemName}>
                      フリーワード
                    </Typography>

                    <Controller
                      render={({ field }) => (
                        <CustomTextfield
                          {...field}
                          placeholder="フリーワードを入力"
                          width={'100%'}
                          error={Boolean(errors.freeWord)}
                          helperText={errors.freeWord?.message}
                        />
                      )}
                      control={control}
                      name="freeWord"
                    />
                  </Box>
                  <Divider sx={styles.searchDivider} />
                  <Box sx={styles.searchItemWrapper}>
                    <Typography sx={styles.searchItemName}>
                      CO2算定の根拠
                    </Typography>
                    <Box sx={styles.serachOptionWrapper}>
                      <Controller
                        render={({ field }) => (
                          <CustomCheckBox
                            {...field}
                            list={co2BasisOptions}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                        control={control}
                        name="calculationBasisType"
                      />
                    </Box>
                  </Box>
                  <Box sx={[styles.searchItemWrapper, { m: '0 0 24px 0' }]}>
                    <Typography sx={styles.searchItemName}>
                      CO2算定の手段
                    </Typography>
                    <Box sx={styles.serachOptionWrapper}>
                      <Controller
                        render={({ field }) => (
                          <CustomCheckBox
                            {...field}
                            list={carbonOffsets}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                        control={control}
                        name="carbonOffsetFlg"
                      />
                    </Box>
                  </Box>
                  <Divider sx={styles.searchDivider} />
                  <Box sx={styles.searchItemWrapperSp}>
                    <Typography sx={styles.searchItemName}>
                      企業の環境認定
                    </Typography>
                    <Box sx={styles.serachOptionWrapper}>
                      <Controller
                        render={({ field }) => (
                          <CustomRadioButton
                            {...field}
                            options={certificationFlgs}
                          />
                        )}
                        control={control}
                        name="certificationFlg"
                      />
                    </Box>
                  </Box>
                  <Divider sx={styles.searchDivider} />
                  <Box sx={styles.searchItemWrapper}>
                    <Typography sx={styles.searchItemName}>
                      おすすめ建材
                    </Typography>
                    <Box sx={styles.serachOptionWrapper}>
                      <Controller
                        render={({ field }) => (
                          <CustomCheckBox
                            {...field}
                            list={recomendMaterials}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                        control={control}
                        name="recommendFlg"
                      />
                    </Box>
                  </Box>
                  <Divider sx={styles.searchDivider} />
                  <Box sx={styles.searchItemWrapper}>
                    <Typography sx={styles.searchItemName}>
                      お気に入り建材
                    </Typography>
                    <Box sx={[styles.serachOptionWrapper, styles.noWrap]}>
                      <Controller
                        render={({ field }) => (
                          <CustomCheckBox
                            {...field}
                            list={favoriteMaterials}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                        control={control}
                        name="favoriteMaterialsFlg"
                      />
                    </Box>
                  </Box>
                  <Divider sx={styles.searchDivider} />
                  <Box sx={[styles.searchItemWrapper, { mb: '0px' }]}>
                    <Typography sx={styles.searchItemName}>評価</Typography>
                    <Box sx={styles.serachOptionWrapper}>
                      <Controller
                        render={({ field }) => (
                          <CustomRadioButton
                            {...field}
                            options={evaluationOptions}
                          />
                        )}
                        control={control}
                        name="evaluation"
                      />
                    </Box>
                  </Box>
                </Grid>
              </>
            </Box>

            <Grid
              item
              flex={1}
              ref={topRef}
              sx={{
                '@media screen and (max-width: 1199px)': {
                  display: searchSpBoxOpen ? 'none' : 'block'
                },
                display: 'block'
              }}
            >
              {/* 建材ヘッダー */}
              <Grid
                container
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent={'space-between'}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                rowGap={'16px'}
                columnGap={'16px'}
                sx={{
                  mt: '32px',
                  '@media screen and (max-width: 600px)': {
                    mt: '8px'
                  }
                }}
              >
                <Grid item>
                  <Typography
                    component="span"
                    sx={{
                      color: gray100,
                      fontSize: '18px',
                      fontWeight: 500,
                      mr: '4px'
                    }}
                  >
                    {total}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ color: gray100, fontSize: '14px', fontWeight: 500 }}
                  >
                    件中 {total > 1 && currentCount.before}
                    {total > 1 && '-'}
                    {currentCount.after}件表示
                  </Typography>
                </Grid>
                <Grid
                  item
                  container
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={'center'}
                  columnGap={'16px'}
                  width={{ xs: '100%', sm: 'unset' }}
                >
                  <Grid
                    sx={[
                      styles.buttonWidth,
                      {
                        '@media screen and (max-width: 600px)': {
                          mb: '8px'
                        }
                      }
                    ]}
                  >
                    <CustomSelectField
                      list={productOrderList}
                      onChange={handleChangeOrder}
                      value={selectedOrder}
                      type={'primary'}
                    />
                  </Grid>
                  <Grid sx={styles.buttonWidth}>
                    <CustomSelectField
                      list={productMethodList}
                      onChange={handleChangeMethod}
                      value={selectedMethod}
                      type={'primary'}
                    />
                  </Grid>
                  <ToggleButtonGroup
                    aria-label="Platform"
                    color="primary"
                    exclusive
                    onChange={handleChange}
                    value={alignment}
                    sx={[
                      styles.listGraphToggle,
                      {
                        '@media screen and (max-width: 960px)': {
                          display: 'none'
                        }
                      }
                    ]}
                  >
                    <ToggleButton value="list" sx={styles.listGraphButton}>
                      <ViewAgendaOutlinedIcon sx={styles.listGraphIcon} />
                    </ToggleButton>
                    <ToggleButton value="graph" sx={styles.listGraphButton}>
                      <AssessmentOutlinedIcon sx={styles.listGraphIcon} />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
              {total === 0 && (
                <>
                  <Divider
                    sx={{
                      mt: '24px',
                      '@media screen and (max-width: 960px)': { mt: '16px' }
                    }}
                  />
                  <Grid
                    container
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                      mt: '96px',
                      '@media screen and (max-width:600px)': {
                        mt: '54px',
                        width: '100%'
                      }
                    }}
                  >
                    <SearchIcon sx={styles.icon} />
                    <Typography
                      sx={{
                        color: gray100,
                        fontSize: '24px',
                        fontWeight: 500,
                        pb: '24px'
                      }}
                      textAlign="center"
                    >
                      条件に一致する建材は見つかりませんでした
                    </Typography>
                  </Grid>
                </>
              )}
              {total > 0 && alignment === 'list' && (
                <>
                  <Box sx={{ mt: '24px' }} />
                  {list.map((item, index) => (
                    <MaterialCard
                      item={item}
                      key={index}
                      buttonSectionType={'materialsList'}
                      mylistReviewSection={true}
                      statusSection={false}
                      addMylistFun={addMylistFun}
                      deleteMylistFun={deleteMylistFun}
                      addFavoriteFun={addFavoriteFun}
                      deleteFavoriteFun={deleteFavoriteFun}
                    />
                  ))}
                  <Box />
                  {total > limit && (
                    <Box sx={styles.paginationWrapper}>
                      <Pagination
                        count={paginationLength}
                        page={page}
                        onChange={(e, page) => {
                          void (async () => {
                            setPage(page);
                          })();
                        }}
                        sx={styles.pagination}
                      />
                    </Box>
                  )}
                </>
              )}
              {total > 0 && alignment === 'graph' && (
                <>
                  <MaterialGraph
                    list={list}
                    addMylistFun={addMylistFun}
                    deleteMylistFun={deleteMylistFun}
                    addFavoriteFun={addFavoriteFun}
                    deleteFavoriteFun={deleteFavoriteFun}
                    offset={limit * (page - 1)}
                  />
                  {total > limit && (
                    <Box sx={styles.paginationWrapper}>
                      <Pagination
                        count={paginationLength}
                        page={page}
                        onChange={(e, page) => {
                          (async () => {
                            setPage(page);
                          })();
                        }}
                        sx={styles.pagination}
                      />
                    </Box>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
        <CustomSnackbar
          autoHideDuration={6000}
          open={open}
          onClose={() => setOpen(false)}
          text={errorText}
          infoText={infoText}
        />
      </Grid>
    </>
  );
};

export default MaterialsList;
